import { ChevronDown, ChevronUp } from "lucide-react";
import { TextDisplay } from "lumir-back-office-ui/RmsTextDisplay";
import { useState, useEffect } from "react";
import CheckboxSelectionControl from "./checkboxSelectionControl";
import { useSearchParams } from "react-router-dom";

// 데이터를 더 명확하게 구조화
const FILTER_CATEGORIES = {
  CAREER: "경력 사항",
  // EMPLOYMENT: "채용 유형",
  PERIOD: "기간 유형",
};

const FILTER_CATEGORIES_KEY = {
  CAREER: "careerLevel",
  // EMPLOYMENT: "employmentType",
  PERIOD: "periodType",
};

const recruitmentTypes = [
  {
    title: FILTER_CATEGORIES.CAREER,
    paramKey: FILTER_CATEGORIES_KEY.CAREER,
    types: [
      { id: 1, name: "경력" },
      { id: 2, name: "신입" },
    ],
  },
  // {
  //   title: FILTER_CATEGORIES.EMPLOYMENT,
  //   paramKey: FILTER_CATEGORIES_KEY.EMPLOYMENT,
  //   types: [
  //     { id: 1, name: "정규직" },
  //     { id: 2, name: "인턴" },
  //   ],
  // },
  {
    title: FILTER_CATEGORIES.PERIOD,
    paramKey: FILTER_CATEGORIES_KEY.PERIOD,
    types: [
      { id: 1, name: "상시" },
      { id: 2, name: "기간" },
    ],
  },
];

const FilterBox = ({ category, children }) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <section className="w-[240px]">
      <div
        className="flex items-center justify-between mb-[16px]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <TextDisplay size="lg" textData={{ primary: category.title }} />
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </div>
      {isOpen && children}
    </section>
  );
};

const FilterSideBar = ({
  setCareerLevel,
  // setEmploymentType,
  setPeriodType,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTypes, setSelectedTypes] = useState({});

  useEffect(() => {
    const initialSelectedTypes = {};
    recruitmentTypes.forEach((category) => {
      const paramValues = searchParams.getAll(category.paramKey);
      if (paramValues.length > 0) {
        if (paramValues.includes("전체")) {
          category.types.forEach((type) => {
            initialSelectedTypes[`${category.title}-${type.id}`] = true;
          });
        } else {
          category.types.forEach((type) => {
            if (paramValues.includes(type.name)) {
              initialSelectedTypes[`${category.title}-${type.id}`] = true;
            } else {
              // Ensure checkboxes are unchecked if not in params
              initialSelectedTypes[`${category.title}-${type.id}`] = false;
            }
          });
        }
      } else {
        // If category not in params, ensure all its types are unselected
        category.types.forEach((type) => {
          initialSelectedTypes[`${category.title}-${type.id}`] = false;
        });
      }
    });
    setSelectedTypes(initialSelectedTypes);
  }, []);

  const handleTotalCheckboxChange = (categoryTitle) => {
    const categoryDetails = recruitmentTypes.find(
      (cat) => cat.title === categoryTitle
    );
    const isCategoryCurrentlyFullySelected = categoryDetails.types.every(
      (type) => selectedTypes[`${categoryTitle}-${type.id}`]
    );
    const newStateForAllInCategory = !isCategoryCurrentlyFullySelected;

    const newSelected = { ...selectedTypes };
    categoryDetails.types.forEach((type) => {
      newSelected[`${categoryTitle}-${type.id}`] = newStateForAllInCategory;
    });
    setSelectedTypes(newSelected);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete(categoryDetails.paramKey); // Clear existing params for this category

    if (newStateForAllInCategory) {
      // All are now selected
      newParams.set(categoryDetails.paramKey, "전체");
    }
    // If !newStateForAllInCategory (all are now unselected), the category remains deleted.
    setSearchParams(newParams);
  };

  const handleCheckboxChange = (categoryTitle, typeId) => {
    const categoryDetails = recruitmentTypes.find(
      (cat) => cat.title === categoryTitle
    );
    const typeObject = categoryDetails.types.find((t) => t.id === typeId);

    if (!typeObject) return; // Should not happen

    const newSelectedState = { ...selectedTypes };
    const selectionKey = `${categoryTitle}-${typeId}`;
    newSelectedState[selectionKey] = !newSelectedState[selectionKey];
    setSelectedTypes(newSelectedState);

    const newParams = new URLSearchParams(searchParams);
    newParams.delete(categoryDetails.paramKey); // Clear existing params for this category

    const currentSelectedNamesForCategory = categoryDetails.types
      .filter((type) => newSelectedState[`${categoryTitle}-${type.id}`])
      .map((type) => type.name);

    const allTypeNamesForCategory = categoryDetails.types.map((t) => t.name);

    if (
      currentSelectedNamesForCategory.length === allTypeNamesForCategory.length
    ) {
      newParams.set(categoryDetails.paramKey, "전체");
    } else if (currentSelectedNamesForCategory.length > 0) {
      currentSelectedNamesForCategory.forEach((name) =>
        newParams.append(categoryDetails.paramKey, name)
      );
    }
    // If currentSelectedNamesForCategory.length === 0, the category is cleared.

    setSearchParams(newParams);
  };

  const isCategoryAllSelected = (categoryTitle) => {
    const categoryTypes = recruitmentTypes.find(
      (cat) => cat.title === categoryTitle
    ).types;
    return categoryTypes.every(
      (type) => selectedTypes[`${categoryTitle}-${type.id}`]
    );
  };

  // 중복 코드 제거를 위한 유틸리티 함수
  const processFilterCategory = (categoryTitle, setter) => {
    const categoryData = recruitmentTypes.find(
      (cat) => cat.title === categoryTitle
    );
    if (!categoryData) return; // Category not found

    const types = categoryData.types;
    const selectedItems = types.filter(
      (type) => selectedTypes[`${categoryTitle}-${type.id}`]
    );

    if (selectedItems.length === 0) {
      setter("");
    } else if (selectedItems.length === types.length) {
      setter("전체");
    } else {
      setter(selectedItems.map((type) => type.name).join(","));
    }
  };

  useEffect(() => {
    // 각 카테고리 처리를 함수로 분리
    processFilterCategory(FILTER_CATEGORIES.CAREER, setCareerLevel);
    // processFilterCategory(FILTER_CATEGORIES.EMPLOYMENT, setEmploymentType);
    processFilterCategory(FILTER_CATEGORIES.PERIOD, setPeriodType);
  }, [selectedTypes, setCareerLevel, setPeriodType]);

  return (
    <section className="w-[240px] h-full space-y-[24px]">
      {recruitmentTypes.map((category) => (
        <FilterBox key={category.title} category={category}>
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-sp80">
              <CheckboxSelectionControl
                id={`total-${category.title}`}
                checked={isCategoryAllSelected(category.title)}
                onChange={() => handleTotalCheckboxChange(category.title)}
                label="전체"
                type="checkbox"
              />
            </div>

            {category.types.map((type) => (
              <div key={type.id} className="flex items-center gap-2 p-sp80">
                <CheckboxSelectionControl
                  id={`type-${category.title}-${type.id}`}
                  checked={
                    selectedTypes[`${category.title}-${type.id}`] || false
                  }
                  onChange={() => handleCheckboxChange(category.title, type.id)}
                  label={type.name}
                  type="checkbox"
                />
              </div>
            ))}
          </div>
        </FilterBox>
      ))}
    </section>
  );
};

export { FilterSideBar };
